import React, { useMemo, useState } from 'react';
import times from 'lodash/times';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Modal } from '../../../../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { DepositForm } from '../../../TransferMoney/components/Deposit/components/DepositForm/DepositForm';
import { TransferMoneyContext } from '../../../TransferMoney/hooks/useTransferMoneyContext';
import {
  DepositMetadata,
  useDepositForm,
} from '../../../TransferMoney/components/Deposit/components/DepositForm/useDepositForm';
import { Shimmer } from '../../../../CommonComponents/OPL/Shimmer/Shimmer';
import { ModalBody } from '../../../../CommonComponents/OPL/Modal/ModalBody';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { ErrorState } from '../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { StatusScreen } from '../../../TransferMoney/components/StatusScreen/StatusScreen';
import { useRouteParams } from '../../hooks/useRouteParams';
import { DepositPreview } from '../../../TransferMoney/components/Deposit/DepositPreview';
import { DepositParams } from '../../../TransferMoney/api/useDepositMutation';

interface FundModalProps {
  onClose(): void;
  onCompleted(): void;
}

export const FundModal: React.FC<FundModalProps> = ({ onClose, onCompleted }) => {
  const [isTransferComplete, setIsTransferComplete] = useState(false);

  const { clientId, draftId } = useRouteParams();

  const context = useMemo(
    () => ({
      clientId,
      managedAccountId: draftId,
      accountId: '',
      accountName: '',
    }),
    [clientId, draftId],
  );

  if (isTransferComplete) {
    return (
      <StatusScreen
        title="Transfer Completed"
        status="success"
        action={{
          label: 'OK',
          onClick: onCompleted,
        }}
      >
        Pending transactions will show in the transaction page once your account is approved.
      </StatusScreen>
    );
  }

  return (
    <TransferMoneyContext.Provider value={context}>
      <Modal isOpen onRequestClose={onClose}>
        <ModalHeader>Fund Account</ModalHeader>
        <ModalCloseButton onClick={onClose} />

        <FundModalContent onSuccess={() => setIsTransferComplete(true)} onClose={onClose} />
      </Modal>
    </TransferMoneyContext.Provider>
  );
};

interface FundModalContentProps {
  onSuccess(): void;
  onClose(): void;
}

const FundModalContent: React.FC<FundModalContentProps> = ({ onSuccess, onClose }) => {
  const [depositDetails, setDepositDetails] = useState<{
    params: DepositParams;
    metadata: DepositMetadata;
  } | null>(null);

  const { isLoading, isError, refetch } = useDepositForm();

  if (isLoading) {
    return (
      <ModalBody>
        {times(3).map((key, index) => (
          <Shimmer key={key} height={64} mt={index !== 0 ? 16 : 0} />
        ))}
      </ModalBody>
    );
  }

  if (isError) {
    return (
      <ModalBody>
        <Box my="56">
          <ErrorState onAction={refetch} />
        </Box>
      </ModalBody>
    );
  }

  return (
    <>
      <Text color="neutral.500" mx="24" mb="16">
        Select an account source to fund the new account through ACH transfer.
      </Text>

      {depositDetails ? (
        <DepositPreview
          depositDetails={depositDetails}
          onBack={() => setDepositDetails(null)}
          onClose={() => setDepositDetails(null)}
          onSuccess={onSuccess}
        />
      ) : (
        <DepositForm isStandalone onClose={onClose} onSuccess={setDepositDetails} />
      )}
    </>
  );
};
